// src/features/landing/charts.tsx
// Hand-rolled SVG visit-trend chart for the page analytics tab — no chart
// library, no new deps. Same approach as features/project/charts.tsx and
// features/analytics/charts.tsx; kept local to this feature rather than
// importing either of those.

import type { VisitTrendPoint } from '@core/types/landing';

const W = 560;
const H = 180;
const PAD = { top: 12, right: 12, bottom: 24, left: 36 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

function niceMax(v: number): number {
  if (v <= 0) return 5;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

export function VisitTrendChart({ points, title, empty }: { points: VisitTrendPoint[]; title: string; empty: string }) {
  if (!points || points.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p>;
  }
  const yMax = niceMax(Math.max(1, ...points.map((p) => p.views)));
  const n = points.length;
  const x = (i: number) => (n === 1 ? PAD.left + PLOT_W / 2 : PAD.left + (PLOT_W * i) / (n - 1));
  const y = (v: number) => PAD.top + PLOT_H - (PLOT_H * Math.max(0, v)) / yMax;
  const line = points.map((p, i) => `${x(i)},${y(p.views)}`).join(' ');
  const area = `${PAD.left},${PAD.top + PLOT_H} ${line} ${x(n - 1)},${PAD.top + PLOT_H}`;

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[360px]" role="img" aria-label={title} preserveAspectRatio="xMidYMid meet">
        <title>{title}</title>
        <line x1={PAD.left} y1={PAD.top + PLOT_H} x2={W - PAD.right} y2={PAD.top + PLOT_H} className="stroke-border" strokeWidth={1} />
        <text x={PAD.left - 6} y={PAD.top + 4} textAnchor="end" className="fill-muted-foreground" fontSize={10}>
          {yMax}
        </text>
        <text x={PAD.left - 6} y={PAD.top + PLOT_H} textAnchor="end" className="fill-muted-foreground" fontSize={10}>
          0
        </text>
        <polygon points={area} className="fill-primary opacity-20" />
        <polyline points={line} fill="none" className="stroke-primary" strokeWidth={2} />
        {points.map((p, i) => (
          <circle key={i} cx={x(i)} cy={y(p.views)} r={2} className="fill-primary" />
        ))}
      </svg>
    </div>
  );
}
